import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_CONFIG, ApiConfig, DEFAULT_API_CONFIG } from './api-config';
import { MARINA_ID_PROVIDER, IMarinaIdProvider } from './marina-id-provider';
import { CUSTOMER_ID_PROVIDER, ICustomerIdProvider } from './customer-id-provider';
import { RAW_HTTP_ERRORS } from './http-context.tokens';

/**
 * Per-request options beyond headers.
 *
 * `rawErrors` opts a request out of `handleError`, so the caller receives the untouched
 * `HttpErrorResponse` with its status and body intact, and stamps {@link RAW_HTTP_ERRORS} so the
 * error interceptor leaves it alone too. Auth endpoints need this: see the token's own note.
 */
export interface ApiRequestOptions {
  rawErrors?: boolean;
  context?: HttpContext;
}

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
  traceId?: string;
}

/**
 * Paged request parameters
 */
export interface PagedRequest {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

/**
 * Paged response wrapper
 */
export interface PagedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages?: number;
}

/**
 * Base API service providing common HTTP operations
 */
@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  protected readonly baseUrl: string;
  protected readonly apiVersion: string;
  protected readonly timeout: number;
  // Use Injector to lazily resolve providers to avoid circular dependency
  private readonly injector: Injector;
  private _marinaIdProvider?: IMarinaIdProvider;
  private _customerIdProvider?: ICustomerIdProvider;

  constructor(protected http: HttpClient) {
    console.log('[BaseApiService] Constructor called');
    // Inject Injector so we can lazily resolve MARINA_ID_PROVIDER later
    this.injector = inject(Injector);
    
    // NOTE:
    // We intentionally use `inject()` here instead of constructor params because many derived services
    // call `super(http)` and Angular DI does not automatically supply base-class constructor params.
    const injectedConfig = inject<ApiConfig | null>(API_CONFIG, { optional: true });
    const apiConfig = injectedConfig ?? DEFAULT_API_CONFIG;

    console.log('[BaseApiService] Config loaded:', {
      hasInjectedConfig: !!injectedConfig,
      injectedConfig,
      apiConfig,
      baseUrl: apiConfig.baseUrl,
    });

    this.baseUrl = apiConfig.baseUrl;
    this.apiVersion = apiConfig.apiVersion || 'v1';
    this.timeout = apiConfig.timeout || 30000;

    // DO NOT inject MARINA_ID_PROVIDER here - it causes circular dependency
    // It will be lazy-loaded via Injector in getHeaders() when actually needed

    console.log('[BaseApiService] Final state:', {
      baseUrl: this.baseUrl,
      apiVersion: this.apiVersion,
      timeout: this.timeout,
    });

    if (!this.baseUrl) {
      // Keep this as a warning (not an exception) so the app still boots, but makes the problem obvious.
      console.warn(
        '[BaseApiService] ⚠️ API baseUrl is empty! Did you forget to provide API_CONFIG in the app module?',
        { injectedConfig, defaultConfig: DEFAULT_API_CONFIG }
      );
    } else {
      console.log('[BaseApiService] ✅ API baseUrl is set:', this.baseUrl);
    }
  }

  /**
   * Lazy-load MARINA_ID_PROVIDER to avoid circular dependency
   * Uses Injector.get() which can be called outside injection context.
   * 
   * IMPORTANT: This is safe because:
   * 1. AuthService (from @marina/auth) is a singleton, so it already exists when this is called
   * 2. We only call this during HTTP requests (getHeaders), not during construction
   * 3. Angular DI will return the existing AuthService instance when resolving MARINA_ID_PROVIDER
   * 
   * This breaks the circular dependency because:
   * - BaseApiService constructor does NOT need MARINA_ID_PROVIDER (it only needs Injector)
   * - MARINA_ID_PROVIDER is only resolved later, after all services are constructed
   */
  private getMarinaIdProvider(): IMarinaIdProvider | undefined {
    if (this._marinaIdProvider === undefined) {
      try {
        this._marinaIdProvider = this.injector.get(MARINA_ID_PROVIDER, null) ?? undefined;
      } catch (error) {
        console.warn('[BaseApiService] Could not resolve MARINA_ID_PROVIDER:', error);
        this._marinaIdProvider = undefined;
      }
    }
    return this._marinaIdProvider;
  }

  private getCustomerIdProvider(): ICustomerIdProvider | undefined {
    if (this._customerIdProvider === undefined) {
      try {
        this._customerIdProvider = this.injector.get(CUSTOMER_ID_PROVIDER, null) ?? undefined;
      } catch (error) {
        console.warn('[BaseApiService] Could not resolve CUSTOMER_ID_PROVIDER:', error);
        this._customerIdProvider = undefined;
      }
    }
    return this._customerIdProvider;
  }

  /**
   * Get full API URL for a given endpoint
   */
  protected getUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    // If baseUrl is empty, this becomes a relative URL (e.g. `/auth/login`) and will hit the web app itself.
    return `${this.baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Get HTTP headers with common options.
   * Automatically adds X-Marina-Id and, when a CUSTOMER_ID_PROVIDER is registered,
   * X-Customer-Id for scoped data access.
   */
  protected getHeaders(additionalHeaders?: Record<string, string>): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    // X-Marina-Id — present when a marina is selected.
    const marinaIdProvider = this.getMarinaIdProvider();
    if (marinaIdProvider && typeof marinaIdProvider.getCurrentMarinaId === 'function') {
      try {
        const marinaId = marinaIdProvider.getCurrentMarinaId();
        if (marinaId) headers = headers.set('X-Marina-Id', marinaId);
      } catch (error) {
        console.warn('[BaseApiService] Error calling getCurrentMarinaId:', error);
      }
    }

    // X-Customer-Id — present when a customer ID provider is registered (customer portal).
    const customerIdProvider = this.getCustomerIdProvider();
    if (customerIdProvider && typeof customerIdProvider.getCurrentCustomerId === 'function') {
      try {
        const customerId = customerIdProvider.getCurrentCustomerId();
        if (customerId) headers = headers.set('X-Customer-Id', customerId);
      } catch (error) {
        console.warn('[BaseApiService] Error calling getCurrentCustomerId:', error);
      }
    }

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  /**
   * Convert PagedRequest to HttpParams
   */
  protected getPagedParams(request?: PagedRequest): HttpParams {
    let params = new HttpParams();

    if (request) {
      if (request.pageNumber !== undefined) {
        params = params.set('pageNumber', request.pageNumber.toString());
      }
      if (request.pageSize !== undefined) {
        params = params.set('pageSize', request.pageSize.toString());
      }
      if (request.searchTerm) {
        params = params.set('searchTerm', request.searchTerm);
      }
      if (request.sortBy) {
        params = params.set('sortBy', request.sortBy);
      }
      if (request.sortDescending !== undefined) {
        params = params.set('sortDescending', request.sortDescending.toString());
      }
    }

    return params;
  }

  /**
   * GET request
   */
  protected get<T>(endpoint: string, params?: HttpParams, headers?: HttpHeaders): Observable<T> {
    const url = this.getUrl(endpoint);
    const options = {
      headers: headers || this.getHeaders(),
      params: params,
      withCredentials: true, // Required for httpOnly cookies
    };

    return this.http.get<ApiResponse<T> | T>(url, options).pipe(
      map((response) => {
        // Handle both wrapped and unwrapped responses
        if (this.isApiResponse(response)) {
          return response.data || (response as unknown as T);
        }
        return response as T;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * GET request for paged results
   */
  protected getPaged<T>(
    endpoint: string,
    request?: PagedRequest,
    headers?: HttpHeaders
  ): Observable<PagedResponse<T>> {
    const params = this.getPagedParams(request);
    return this.get<PagedResponse<T>>(endpoint, params, headers);
  }

  /**
   * POST request
   */
  protected post<T>(endpoint: string, body: any, headers?: HttpHeaders, options?: ApiRequestOptions): Observable<T> {
    const url = this.getUrl(endpoint);
    
    // If body is FormData, don't set Content-Type header (let browser set it with boundary)
    // Otherwise, use default headers with Content-Type: application/json
    let requestHeaders: HttpHeaders;
    if (body instanceof FormData) {
      // For FormData, merge headers but don't set Content-Type
      // Browser will automatically set Content-Type with boundary
      const defaultHeaders = this.getHeaders();
      // Copy all headers except Content-Type
      let mergedHeaders = new HttpHeaders();
      defaultHeaders.keys().forEach(key => {
        if (key.toLowerCase() !== 'content-type') {
          mergedHeaders = mergedHeaders.set(key, defaultHeaders.get(key)!);
        }
      });
      // Add any additional headers from provided headers
      if (headers) {
        headers.keys().forEach(key => {
          if (key.toLowerCase() !== 'content-type') {
            mergedHeaders = mergedHeaders.set(key, headers.get(key)!);
          }
        });
      }
      requestHeaders = mergedHeaders;
    } else {
      // For non-FormData, use provided headers or default headers
      requestHeaders = headers || this.getHeaders();
    }
    
    const requestOptions = {
      headers: requestHeaders,
      withCredentials: true, // Required for httpOnly cookies
      context: this.buildContext(options),
    };

    const request$ = this.http.post<ApiResponse<T> | T>(url, body, requestOptions).pipe(
      map((response) => {
        if (this.isApiResponse(response)) {
          return response.data || (response as unknown as T);
        }
        return response as T;
      })
    );

    // Deliberately conditional: handleError turns an HttpErrorResponse into a plain Error carrying
    // only a message, which is exactly what a caller that has to classify the failure cannot use.
    return options?.rawErrors ? request$ : request$.pipe(catchError(this.handleError));
  }

  /**
   * Request context, carrying the raw-errors marker when asked for.
   */
  private buildContext(options?: ApiRequestOptions): HttpContext {
    const context = options?.context ?? new HttpContext();
    return options?.rawErrors ? context.set(RAW_HTTP_ERRORS, true) : context;
  }

  /**
   * PUT request
   */
  protected put<T>(endpoint: string, body: any, headers?: HttpHeaders): Observable<T> {
    const url = this.getUrl(endpoint);
    const options = {
      headers: headers || this.getHeaders(),
      withCredentials: true, // Required for httpOnly cookies
    };

    return this.http.put<ApiResponse<T> | T>(url, body, options).pipe(
      map((response) => {
        if (this.isApiResponse(response)) {
          return response.data || (response as unknown as T);
        }
        return response as T;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * PATCH request
   */
  protected patch<T>(endpoint: string, body: any, headers?: HttpHeaders): Observable<T> {
    const url = this.getUrl(endpoint);
    const options = {
      headers: headers || this.getHeaders(),
      withCredentials: true, // Required for httpOnly cookies
    };

    return this.http.patch<ApiResponse<T> | T>(url, body, options).pipe(
      map((response) => {
        if (this.isApiResponse(response)) {
          return response.data || (response as unknown as T);
        }
        return response as T;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * DELETE request
   */
  protected deleteRequest<T>(endpoint: string, headers?: HttpHeaders): Observable<T> {
    const url = this.getUrl(endpoint);
    const options = {
      headers: headers || this.getHeaders(),
      withCredentials: true, // Required for httpOnly cookies
    };

    return this.http.delete<ApiResponse<T> | T>(url, options).pipe(
      map((response) => {
        if (this.isApiResponse(response)) {
          return response.data || (response as unknown as T);
        }
        return response as T;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Check if response is wrapped in ApiResponse
   */
  private isApiResponse<T>(response: any): response is ApiResponse<T> {
    return response && (response.data !== undefined || response.error !== undefined);
  }

  /**
   * Handle HTTP errors
   */
  protected handleError = (error: HttpErrorResponse | Error): Observable<never> => {
    // If already transformed by interceptor (plain Error), rethrow as-is
    if (error instanceof Error && !(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    const httpError = error as HttpErrorResponse;
    let errorMessage = 'An unknown error occurred';

    if (httpError.error instanceof ErrorEvent) {
      errorMessage = `Error: ${httpError.error.message}`;
    } else {
      errorMessage =
        (typeof httpError.error === 'string' ? httpError.error : null) ||
        httpError.error?.message ||
        httpError.error?.error ||
        `Error Code: ${httpError.status}\nMessage: ${httpError.message}`;
    }

    console.error('API Error:', errorMessage, httpError);
    return throwError(() => new Error(errorMessage));
  };
}

